/**
 * AP-1166 user activity tracking.
 *
 * Sends `activity_*` events to `POST /v2/storefronts/{id}/log-events`. The
 * backend validates access, resolves stable numbers (product / entity / asset
 * number) from the IDs sent here, and always answers 201, so callers never need
 * to handle the response. Sending is best-effort: failures are swallowed.
 */
import axios from "axios";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { endpoints } from "../endpoints";
import type { AssetContainerType } from "./assetContainer.types";
import { useStoreState } from "./util";

export type ActivityEventType =
  | "activity_profile_view"
  | "activity_asset_view"
  | "activity_asset_link_open"
  | "activity_external_link_open"
  | "activity_asset_download";

/** The profile an event happened on. Same values as AssetContainerType. */
export type ActivityProfile = {
  type: AssetContainerType;
  id: string;
};

export type ActivityEvent = {
  event_type: ActivityEventType;
  /** Asset or external link UUID; not used for profile views. */
  object_id?: string;
  profile?: ActivityProfile;
  /**
   * One UUID per user action. Generated when omitted. Only reuse one when
   * retrying the same action, so the backend can drop the duplicate.
   */
  action_id?: string;
};

export const postActivityEvent = async (
  storefrontId: string | undefined,
  { event_type, object_id, profile, action_id }: ActivityEvent
): Promise<void> => {
  if (!storefrontId) return;
  try {
    await axios.post(
      endpoints.v2_storefronts_storefront_id_log_events(storefrontId),
      {
        event_type,
        action_id: action_id ?? uuidv4(),
        ...(object_id && { object_id }),
        ...(profile && { profile_type: profile.type, profile_id: profile.id }),
      }
    );
  } catch (error) {
    // Best-effort: activity tracking must never affect the user.
  }
};

/** Returns a stable function that sends an activity event for the current storefront. */
export const useLogActivityEvent = () => {
  const { storefront_id } = useStoreState();
  return useCallback(
    (event: ActivityEvent) => {
      void postActivityEvent(storefront_id, event);
    },
    [storefront_id]
  );
};

/**
 * Logs one `activity_profile_view` per profile shown: on mount and whenever the
 * profile changes, but not on data revalidation (the effect only depends on
 * the profile's type and id).
 */
export const useLogProfileView = (profile: ActivityProfile | undefined) => {
  const logActivity = useLogActivityEvent();
  const type = profile?.type;
  const id = profile?.id;
  useEffect(() => {
    if (type && id) {
      logActivity({
        event_type: "activity_profile_view",
        profile: { type, id },
      });
    }
  }, [type, id, logActivity]);
};

type ActivityTrackingContextValue = {
  /** Profile that assets and links rendered below belong to, if any. */
  profile?: ActivityProfile;
  /** True in authoring flows (e.g. the new product wizard) where nothing is tracked. */
  disabled?: boolean;
};

const ActivityTrackingContext = createContext<ActivityTrackingContextValue>({});

export const ActivityTrackingProvider = ({
  profile,
  disabled,
  children,
}: ActivityTrackingContextValue & { children: ReactNode }) => {
  const type = profile?.type;
  const id = profile?.id;
  // Keyed on primitives so consumers' effects don't re-run on every render
  const value = useMemo(
    () => ({ profile: type && id ? { type, id } : undefined, disabled }),
    [type, id, disabled]
  );
  return (
    <ActivityTrackingContext.Provider value={value}>
      {children}
    </ActivityTrackingContext.Provider>
  );
};

/**
 * Like useLogActivityEvent, but fills in the profile from the nearest
 * ActivityTrackingProvider and does nothing where tracking is disabled.
 */
export const useLogActivityInContext = () => {
  const { profile, disabled } = useContext(ActivityTrackingContext);
  const logActivity = useLogActivityEvent();
  return useCallback(
    (event: Omit<ActivityEvent, "profile">) => {
      if (disabled) return;
      logActivity({ ...event, profile });
    },
    [disabled, logActivity, profile]
  );
};

export const useActivityTrackingContext = () =>
  useContext(ActivityTrackingContext);
